import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, Award, Download, ChevronLeft, Lock, Video, Monitor, CalendarClock, FileText, GraduationCap, XCircle } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import CourseQuiz from "@/components/CourseQuiz";

export default function MyCourseDetail() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/my/courses/${courseId}`)
      .then((r) => {
        setCourse(r.data);
        if (!activeLessonId && r.data.lessons?.length) {
          const firstIncomplete = r.data.lessons.find(
            (l) => !r.data.completed_lesson_ids.includes(l.lesson_id)
          );
          setActiveLessonId((firstIncomplete || r.data.lessons[0]).lesson_id);
        }
      })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Eğitim yüklenemedi."));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const activeLesson = useMemo(
    () => course?.lessons?.find((l) => l.lesson_id === activeLessonId) || null,
    [course, activeLessonId]
  );

  const completedSet = useMemo(() => new Set(course?.completed_lesson_ids || []), [course]);

  const toggleComplete = async (lessonId, completed) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/my/courses/${courseId}/lessons/${lessonId}`, { completed });
      setCourse((c) => ({ ...c, completed_lesson_ids: data.completed_lesson_ids, progress_percent: data.progress_percent, certificate_id: data.certificate_id }));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  if (err) {
    return (
      <div className="container-x py-24 text-center min-h-[60vh]">
        <div className="bg-red-950/40 border border-red-800 text-red-200 p-4 text-sm inline-block">{err}</div>
        <div className="mt-6"><Link to="/egitimlerim" className="btn-outline text-sm inline-flex"><ChevronLeft className="w-4 h-4" /> Eğitimlerime Dön</Link></div>
      </div>
    );
  }

  if (!course) {
    return <div className="container-x py-24 text-center text-white/50 min-h-[60vh]">Yükleniyor...</div>;
  }

  return (
    <div className="container-x py-10 md:py-14 min-h-[70vh]">
      <Link to="/egitimlerim" className="text-xs text-white/50 hover:text-gold inline-flex items-center gap-1 mb-6">
        <ChevronLeft className="w-3.5 h-3.5" /> Eğitimlerim
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <span className="section-eyebrow">{course.category}</span>
          <h1 className="font-display text-3xl md:text-4xl mt-2">{course.title}</h1>
        </div>
        {course.certificate_id && (
          <a
            href={`${API}/certificates/${course.certificate_id}/pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-certificate-btn"
            className="btn-primary text-sm whitespace-nowrap"
          >
            <Download className="w-4 h-4" /> Sertifikanı İndir
          </a>
        )}
        {!course.certificate_id && course.progress_percent === 100 && course.has_quiz && (
          <a
            href="#sertifika-sinavi"
            data-testid="go-to-quiz-btn"
            className="btn-primary text-sm whitespace-nowrap"
          >
            <GraduationCap className="w-4 h-4" /> Sertifika Sınavına Gir
          </a>
        )}
      </div>

      <div className="flex items-center gap-4 mb-10">
        <div className="h-2 bg-white/10 flex-1">
          <div className="h-2 bg-gold transition-all" style={{ width: `${course.progress_percent}%` }} />
        </div>
        <span className="text-sm text-gold font-display w-14 text-right">%{course.progress_percent}</span>
      </div>

      {(course.google_meet_url || course.virtual_windows_url || course.schedule_text) && (
        <div className="bg-surface border border-gold-30 p-5 mb-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {course.google_meet_url && (
            <a href={course.google_meet_url} target="_blank" rel="noreferrer" data-testid="member-meet-link"
              className="flex items-center gap-3 p-3 border border-white/10 hover:border-gold-30 transition-colors">
              <Video className="w-5 h-5 text-gold flex-shrink-0" />
              <div>
                <div className="text-sm font-medium">Canlı Ders</div>
                <div className="text-xs text-white/50">Google Meet'e katıl</div>
              </div>
            </a>
          )}
          {course.virtual_windows_url && (
            <a href={course.virtual_windows_url} target="_blank" rel="noreferrer" data-testid="member-virtual-windows-link"
              className="flex items-center gap-3 p-3 border border-white/10 hover:border-gold-30 transition-colors">
              <Monitor className="w-5 h-5 text-gold flex-shrink-0" />
              <div>
                <div className="text-sm font-medium">Sanal Windows</div>
                <div className="text-xs text-white/50">Uygulama ortamını indir</div>
              </div>
            </a>
          )}
          {course.schedule_text && (
            <div className="flex items-start gap-3 p-3 border border-white/10">
              <CalendarClock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium mb-1">Ders Programı</div>
                <div className="text-xs text-white/60 whitespace-pre-line">{course.schedule_text}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* Video / player */}
        <div>
          <div className="aspect-video border border-gold-30 bg-black">
            {activeLesson?.embed_url ? (
              <iframe
                key={activeLesson.lesson_id}
                className="w-full h-full"
                src={activeLesson.embed_url}
                title={activeLesson.title}
                allow="autoplay"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/40 text-sm gap-2">
                <Lock className="w-4 h-4" /> Bu ders için video henüz eklenmedi
              </div>
            )}
          </div>
          <div className="mt-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-gold/70 mb-1">{activeLesson?.module_title}</div>
              <div className="font-display text-xl">{activeLesson?.title}</div>
            </div>
            {activeLesson && (
              <button
                disabled={saving}
                onClick={() => toggleComplete(activeLesson.lesson_id, !completedSet.has(activeLesson.lesson_id))}
                data-testid="toggle-lesson-complete-btn"
                className={completedSet.has(activeLesson.lesson_id) ? "btn-outline text-sm whitespace-nowrap" : "btn-primary text-sm whitespace-nowrap"}
              >
                {completedSet.has(activeLesson.lesson_id) ? (
                  <><CheckCircle2 className="w-4 h-4" /> Tamamlandı</>
                ) : (
                  <><Circle className="w-4 h-4" /> Tamamlandı Olarak İşaretle</>
                )}
              </button>
            )}
          </div>
          {activeLesson?.document_url && (
            <a
              href={activeLesson.document_url}
              target="_blank"
              rel="noreferrer"
              data-testid="lesson-document-link"
              className="mt-3 inline-flex items-center gap-2 text-sm text-gold hover:text-gold-light"
            >
              <FileText className="w-4 h-4" /> Ders Dökümanını İndir
            </a>
          )}
        </div>

        {/* Lesson list */}
        <div className="bg-surface border border-white/10 max-h-[70vh] overflow-y-auto">
          {Object.entries(groupByModule(course.lessons)).map(([moduleTitle, lessons]) => (
            <div key={moduleTitle}>
              <div className="px-5 py-3 bg-black/30 text-xs uppercase tracking-widest text-white/50 sticky top-0">{moduleTitle}</div>
              {lessons.map((l) => {
                const done = completedSet.has(l.lesson_id);
                const active = l.lesson_id === activeLessonId;
                return (
                  <button
                    key={l.lesson_id}
                    onClick={() => setActiveLessonId(l.lesson_id)}
                    data-testid={`lesson-item-${l.lesson_id}`}
                    className={`w-full text-left px-5 py-3.5 flex items-center gap-3 border-b border-white/5 transition-colors ${active ? "bg-gold/10" : "hover:bg-white/[0.03]"}`}
                  >
                    {done ? (
                      <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-white/30 flex-shrink-0" />
                    )}
                    <span className={`text-sm flex-1 ${active ? "text-gold" : "text-white/80"}`}>{l.title}</span>
                    {l.duration && <span className="text-[11px] text-white/30">{l.duration}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {course.has_quiz && course.progress_percent === 100 && (
        <div id="sertifika-sinavi" className="mt-14 scroll-mt-24">
          <CourseQuiz
            courseId={courseId}
            certificateId={course.certificate_id}
            onPassed={(certId) => setCourse((c) => ({ ...c, certificate_id: certId }))}
          />
        </div>
      )}
    </div>
  );
}

function groupByModule(lessons) {
  const groups = {};
  for (const l of lessons || []) {
    const key = l.module_title || "Modül";
    if (!groups[key]) groups[key] = [];
    groups[key].push(l);
  }
  return groups;
}
